import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, Redirect } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button, IconArrow } from 'gatorcomponents';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  NEW_SUPPORT,
  ENABLE_SUPPORT_SERVICE_CONTROL,
} from '@/config/GrowthBook/constants';
import { SUPPORT_DEPARTMENT_FINANCIAL } from '@/enums/support/support.enum';
import useLocale from '@/hooks/useLocale';
import mountZendeskTags from '@/components/Buttons/Prechat/utils/mountZendeskTags';
import { billingActions, commonActions } from '@/redux/modules';
import Loader from '@/components/Layout/Loader';
import * as Styles from './CancelWithSupport.styles';
import supportIllustration from './midia/support-illustration.png';

const CancelWithSupport = ({
  featureFlag,
  zendeskTag,
  redirectRoute,
}) => {
  const { subscriptionId, subscriptionType } = useParams();
  const dispatch = useDispatch();
  const summaryState = useSelector(state => state.summary);
  const subscriptions = useSelector(state => state.billing.subscriptions.items);
  const loading = useSelector(state => state.common.loading);
  const [subscription, setSubscription] = useState(undefined);
  const { billing: billingLocale, preChat: preChatLocale, routes: routesLocale } = useLocale();
  const { humanSupport: humanSupportLocale } = billingLocale.subscriptions.cancel;

  const isHumanSupportEnabled = useFeatureIsOn(featureFlag);
  const useNewSupport = useFeatureIsOn(NEW_SUPPORT);
  const enableDepartmentVerification = useFeatureIsOn(ENABLE_SUPPORT_SERVICE_CONTROL);
  const serverTime = useSelector(state => state.common.serverTime);
  const [isDepartmentAvailable, setIsDepartmentAvailable] = useState(null);

  useEffect(() => {
    if (!subscriptions.length) {
      dispatch(billingActions.subscriptions.request());
    }
  }, [subscriptions.length, dispatch]);

  useEffect(() => {
    if (subscriptions.length) {
      subscriptions
        .filter(({ id, type }) => id === Number(subscriptionId) && type === subscriptionType)
        .forEach(item => setSubscription(item));
    }
  }, [subscriptionId, subscriptionType, subscriptions]);

  useEffect(() => {
    if (enableDepartmentVerification) {
      if (!serverTime || !serverTime.departments) {
        dispatch(commonActions.serverTime.request());
        return;
      }

      const financialDepartment = serverTime.departments.find(
        dep => dep.slug === SUPPORT_DEPARTMENT_FINANCIAL,
      );

      const isAvailable = financialDepartment && financialDepartment.status === 'open';
      setIsDepartmentAvailable(isAvailable);
    } else {
      setIsDepartmentAvailable(true);
    }
  }, [serverTime, enableDepartmentVerification, dispatch]);

  const getSubscriptionInfo = () => {
    if (subscription) {
      const { type, domain } = subscription;
      const planName = type === 'domain'
        ? billingLocale.domainLabel
        : subscription.productName;

      return {
        planName,
        domain: domain || '',
      };
    }

    return {
      planName: '',
      domain: '',
    };
  };

  const openChatWithSupport = useCallback(async () => {
    const { id: whmcsId } = summaryState;

    const tags = await mountZendeskTags({
      department: 'financial',
      primaryReason: null,
      secondaryReason: null,
      product: subscriptionId,
      whmcsId,
      departmentLocales: preChatLocale.zopimDepartments,
      v2: useNewSupport,
    });

    tags.push(zendeskTag);

    if (window.zE) {
      window.zE('messenger:set', 'conversationTags', tags);
      await new Promise(resolve => setTimeout(resolve, 500));
      window.zE('messenger', 'open');
    }
  }, [summaryState, preChatLocale, useNewSupport, subscriptionId, zendeskTag]);

  const shouldRedirect = !isHumanSupportEnabled || (enableDepartmentVerification && isDepartmentAvailable === false);

  if (shouldRedirect) {
    const redirectPath = `${routesLocale.manageSubscription}${routesLocale[redirectRoute]}/${subscriptionId}/${subscriptionType}`;
    return (
      <Redirect
        to={redirectPath}
      />
    );
  }

  const isCheckingDepartment = enableDepartmentVerification && isDepartmentAvailable === null;
  if (loading || !subscription || isCheckingDepartment) {
    return <Loader />;
  }

  const subscriptionInfo = getSubscriptionInfo();

  return (
    <Styles.PageWrapper>
      <Styles.SubscriptionHeader>
        <Styles.SubscriptionPlanName>{subscriptionInfo.planName}</Styles.SubscriptionPlanName>
        {subscriptionInfo.domain && (
          <>
            <Styles.SubscriptionSeparator>-</Styles.SubscriptionSeparator>
            <Styles.SubscriptionDomainName>{subscriptionInfo.domain}</Styles.SubscriptionDomainName>
          </>
        )}
      </Styles.SubscriptionHeader>

      <Styles.Wrapper data-testid="cancel-with-support-handler">
        <Styles.BackgroundGlow1 />
        <Styles.BackgroundGlow2 />

        <Styles.Container>
          <Styles.ContentHeader>
            <Styles.IllustrationWrapper>
              <Styles.Illustration
                src={supportIllustration}
                alt={humanSupportLocale.imageAlt}
              />
            </Styles.IllustrationWrapper>

            <Styles.ContentText>
              <Styles.Title>{humanSupportLocale.title}</Styles.Title>
              <Styles.DescriptionWrapper>
                <Styles.Description>{humanSupportLocale.description[0]}</Styles.Description>
                <Styles.Description>{humanSupportLocale.description[1]}</Styles.Description>
              </Styles.DescriptionWrapper>
            </Styles.ContentText>
          </Styles.ContentHeader>

          <Styles.ContentButton>
            <Styles.ButtonWrapper>
              <Button
                testId="cancel-with-support-button"
                label={humanSupportLocale.buttonLabel}
                variant="primary"
                size="large"
                onClick={openChatWithSupport}
                iconRight={<IconArrow direction="right" size="lg" color="primaryBlue" />}
              />
            </Styles.ButtonWrapper>
            <Styles.Subtitle>{humanSupportLocale.subtitle}</Styles.Subtitle>
          </Styles.ContentButton>
        </Styles.Container>
      </Styles.Wrapper>
    </Styles.PageWrapper>
  );
};

CancelWithSupport.propTypes = {
  featureFlag: PropTypes.string.isRequired,
  zendeskTag: PropTypes.string.isRequired,
  redirectRoute: PropTypes.string.isRequired,
};

export default CancelWithSupport;
